"use client";

import { useState } from "react";
import Image from "next/image";
import Link from "next/link";
import {
  Search,
  Sparkles,
  ArrowRight,
  Star,
  Tag,
  ShieldCheck,
  Truck,
  RotateCcw,
  Headphones,
  ChevronDown,
  Flame,
  Calendar,
  CheckCircle2,
} from "lucide-react";

interface SoftStorefrontProps {
  subdomain: string;
  settings: any;
  products?: any[];
  categories?: any[];
  banners?: any[];
  testimonials?: any[];
  blogs?: any[];
  faqs?: any[];
}

export default function SoftClassifiedStorefront({
  subdomain,
  settings,
  products = [],
  categories = [],
  banners = [],
  testimonials = [],
  blogs = [],
  faqs = [],
}: SoftStorefrontProps) {
  const [searchQuery, setSearchQuery] = useState("");
  const [activeCategory, setActiveCategory] = useState<string>("all");
  const [activeFaq, setActiveFaq] = useState<number | null>(null);

  // 🌟 ব্যাকএন্ডের রিলেটিভ ইমেজ ও ফলব্যাক সুন্দরভাবে হ্যান্ডেল করার ফাংশন
  const resolveImg = (url: string | null | undefined, fallback: string) => {
    if (!url) return fallback;
    if (url.startsWith("http://") || url.startsWith("https://")) return url;
    const cleanPath = url.startsWith("/") ? url : `/${url}`;
    return `http://${subdomain}.localhost:8000${cleanPath}`;
  };

  // 🌟 ক্যাটাগরি অনুযায়ী প্রফেশনাল ইমেজ ফলব্যাক
  const getCategoryFallbackImage = (name: string) => {
    const n = (name || "").toLowerCase();
    if (n.includes("bag") || n.includes("wallet"))
      return "https://images.unsplash.com/photo-1548036328-c9fa89d128fa?w=300&auto=format&fit=crop&q=80";
    if (n.includes("beauty") || n.includes("personal") || n.includes("care"))
      return "https://images.unsplash.com/photo-1522337360788-8b13dee7a37e?w=300&auto=format&fit=crop&q=80";
    if (n.includes("food") || n.includes("health") || n.includes("grocery"))
      return "https://images.unsplash.com/photo-1610348725531-843dff563e2c?w=300&auto=format&fit=crop&q=80";
    if (n.includes("jewel") || n.includes("access"))
      return "https://images.unsplash.com/photo-1599643478518-a784e5dc4c8f?w=300&auto=format&fit=crop&q=80";
    if (n.includes("kid") || n.includes("baby"))
      return "https://images.unsplash.com/photo-1515488042361-ee00e0ddd4e4?w=300&auto=format&fit=crop&q=80";
    if (n.includes("men"))
      return "https://images.unsplash.com/photo-1617137984095-74e4e5e3613f?w=300&auto=format&fit=crop&q=80";
    if (n.includes("women"))
      return "https://images.unsplash.com/photo-1483985988355-763728e1935b?w=300&auto=format&fit=crop&q=80";
    if (n.includes("shoe") || n.includes("footwear"))
      return "https://images.unsplash.com/photo-1549298916-b41d501d3772?w=300&auto=format&fit=crop&q=80";
    return "https://images.unsplash.com/photo-1523275335684-37898b6baf30?w=300&auto=format&fit=crop&q=80";
  };

  const filteredProducts = products.filter((p) => {
    const matchSearch = (p.name || "").toLowerCase().includes(searchQuery.toLowerCase());
    const matchCat =
      activeCategory === "all" ||
      String(p.category?.id || p.category) === String(activeCategory);
    return matchSearch && matchCat;
  });

  const featured = filteredProducts.filter((p) => p.is_featured);
  const displayFeatured = featured.length > 0 ? featured : filteredProducts;
  const bestsellers = filteredProducts.filter((p) => p.is_bestseller);
  const countdownBanner = banners.find((b) => b.banner_type === "countdown") || null;
  const deals = filteredProducts.filter((p) => Boolean(p.is_deal_of_day));

  return (
    <div className="min-h-screen bg-[#F0F4F9] text-slate-800 font-sans pb-28 selection:bg-blue-200 space-y-12">
      {/* 🌟 ১. 3D Floating Hero */}
      <section className="relative pt-12 pb-16 px-4 overflow-hidden text-center">
        <div className="absolute top-10 left-12 w-28 h-28 bg-blue-300/30 rounded-full blur-2xl pointer-events-none animate-pulse" />
        <div className="absolute top-24 right-16 w-36 h-36 bg-indigo-300/25 rounded-full blur-3xl pointer-events-none" />

        <div className="max-w-4xl mx-auto space-y-4 relative z-10">
          <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-white/90 shadow-[4px_4px_12px_#d9e2ec,-4px_-4px_12px_#ffffff] border border-white/80 text-blue-600 text-xs font-black uppercase tracking-wider">
            <Sparkles className="w-3.5 h-3.5 text-blue-500" />
            <span>Classified & Direct Store Hub</span>
          </div>

          <h1 className="text-3xl sm:text-5xl lg:text-6xl font-black text-slate-900 tracking-tight leading-tight">
            {settings?.store_name || "ClassiAds Marketplace"}
          </h1>

          <p className="text-sm sm:text-base text-slate-500 max-w-xl mx-auto font-medium">
            {settings?.store_tagline || "Discover verified products, organic groceries, and classified deals."}
          </p>

          <div className="max-w-2xl mx-auto pt-4">
            <div className="bg-white p-2 sm:p-2.5 rounded-3xl sm:rounded-full shadow-[8px_8px_20px_#d5e0ec,-8px_-8px_20px_#ffffff] border border-white flex flex-col sm:flex-row items-center gap-2">
              <div className="flex items-center gap-3 px-4 w-full flex-1">
                <Search className="w-5 h-5 text-slate-400 flex-shrink-0" />
                <input
                  type="text"
                  placeholder="Search products or classified items..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full text-sm bg-transparent outline-none text-slate-800 placeholder-slate-400 font-medium"
                />
              </div>
              <button className="w-full sm:w-auto px-8 py-3 rounded-full bg-blue-600 hover:bg-blue-500 text-white text-xs font-black tracking-wide shadow-lg shadow-blue-500/30 transition-all cursor-pointer">
                Search
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* 🌟 ২. ক্যাটাগরি সেকশন (অক্ষরের পরিবর্তে ছবি সহ) */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6">
        <div className="text-center mb-8">
          <h2 className="text-2xl font-black text-slate-900 tracking-tight">Popular Categories</h2>
          <p className="text-xs text-slate-500 mt-1">Browse our verified department collections</p>
        </div>

        <div className="grid grid-cols-2 sm:grid-cols-4 md:grid-cols-6 lg:grid-cols-8 gap-4">
          <button
            onClick={() => setActiveCategory("all")}
            className={`p-4 rounded-3xl transition-all text-center flex flex-col items-center justify-center gap-2.5 cursor-pointer border ${
              activeCategory === "all"
                ? "bg-blue-600 text-white shadow-xl shadow-blue-500/30 border-blue-600 -translate-y-1"
                : "bg-white text-slate-700 shadow-[6px_6px_14px_#dce5ef,-6px_-6px_14px_#ffffff] border-white hover:-translate-y-1"
            }`}
          >
            <div className="relative w-12 h-12 rounded-2xl overflow-hidden shadow-inner flex items-center justify-center bg-blue-50">
              <Sparkles className="w-6 h-6 text-blue-600" />
            </div>
            <span className="text-xs font-bold truncate max-w-full">All Items</span>
          </button>

          {categories.map((c: any) => {
            const isSelected = String(activeCategory) === String(c.id);
            const catImg = resolveImg(c.image || c.icon, getCategoryFallbackImage(c.name));

            return (
              <button
                key={c.id}
                onClick={() => setActiveCategory(String(c.id))}
                className={`p-4 rounded-3xl transition-all text-center flex flex-col items-center justify-center gap-2.5 cursor-pointer border ${
                  isSelected
                    ? "bg-blue-600 text-white shadow-xl shadow-blue-500/30 border-blue-600 -translate-y-1"
                    : "bg-white text-slate-700 shadow-[6px_6px_14px_#dce5ef,-6px_-6px_14px_#ffffff] border-white hover:-translate-y-1"
                }`}
              >
                <div className="relative w-12 h-12 rounded-2xl overflow-hidden shadow-sm bg-slate-50">
                  <Image
                    src={catImg}
                    alt={c.name}
                    fill
                    unoptimized
                    className="object-cover"
                  />
                </div>
                <span className="text-xs font-bold truncate max-w-full">{c.name}</span>
              </button>
            );
          })}
        </div>
      </section>

      {/* 🌟 ৩. Featured Classified Products */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6">
        <div className="flex items-center justify-between mb-8">
          <div>
            <span className="text-[11px] font-black uppercase tracking-wider text-blue-600">
              Verified Store Stock
            </span>
            <h2 className="text-xl sm:text-2xl font-black text-slate-900">
              Featured Classified Listings
            </h2>
          </div>
          <span className="text-xs font-bold text-slate-500 bg-white px-4 py-2 rounded-full shadow-[4px_4px_10px_#dce5ef,-4px_-4px_10px_#ffffff]">
            {displayFeatured.length} Items Listed
          </span>
        </div>

        {displayFeatured.length === 0 ? (
          <div className="bg-white rounded-3xl p-16 text-center shadow-[6px_6px_16px_#dce5ef,-6px_-6px_16px_#ffffff] border border-white">
            <Tag className="w-12 h-12 text-slate-300 mx-auto mb-3" />
            <p className="text-sm font-bold text-slate-600">কোনো প্রোডাক্ট পাওয়া যায়নি।</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {displayFeatured.map((product: any) => {
              const price = Number(product.price || 0);
              const oldPrice = Number(product.old_price || product.regular_price || 0);
              const productImg = resolveImg(
                product.image_url || product.image || product.primary_image,
                "https://images.unsplash.com/photo-1542838132-92c53300491e?w=600"
              );

              return (
                <div
                  key={product.id}
                  className="bg-white rounded-3xl p-4 shadow-[8px_8px_20px_#dce5ef,-8px_-8px_20px_#ffffff] border border-white/90 hover:-translate-y-1.5 transition-all flex flex-col justify-between group"
                >
                  <div>
                    <div className="relative w-full h-52 bg-slate-50 rounded-2xl overflow-hidden mb-3.5">
                      <Image
                        src={productImg}
                        alt={product.name}
                        fill
                        unoptimized
                        className="object-cover group-hover:scale-105 transition-transform duration-500"
                      />
                      <span className="absolute top-2.5 left-2.5 px-2.5 py-1 rounded-full bg-blue-600 text-white text-[10px] font-black uppercase tracking-wider shadow-md">
                        Verified
                      </span>
                    </div>

                    <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wider block">
                      {product.category?.name || "Marketplace"}
                    </span>
                    <h3 className="text-sm font-bold text-slate-900 mt-1 line-clamp-1 group-hover:text-blue-600 transition-colors">
                      {product.name}
                    </h3>
                  </div>

                  <div className="pt-4 mt-4 border-t border-slate-100 flex items-center justify-between">
                    <div>
                      <div className="text-base font-black text-blue-600 font-mono">
                        ৳{price.toLocaleString()}
                      </div>
                      {oldPrice > price && (
                        <span className="text-[11px] text-slate-400 line-through font-mono">
                          ৳{oldPrice.toLocaleString()}
                        </span>
                      )}
                    </div>

                    <Link
                      href={`/products/${product.slug || product.id}`}
                      className="w-10 h-10 rounded-2xl bg-blue-50 hover:bg-blue-600 text-blue-600 hover:text-white flex items-center justify-center transition-colors shadow-sm cursor-pointer"
                    >
                      <ArrowRight className="w-4 h-4" />
                    </Link>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </section>

      {/* 🌟 ৪. কাউন্টডাউন ব্যানার */}
      {countdownBanner && (
        <section className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="bg-gradient-to-r from-blue-600 via-indigo-600 to-sky-600 rounded-[36px] p-8 sm:p-12 text-white shadow-2xl shadow-blue-500/20 flex flex-col md:flex-row items-center justify-between gap-8">
            <div className="space-y-3 text-center md:text-left">
              <span className="px-3 py-1 rounded-full bg-white/20 text-white text-[10px] font-black uppercase tracking-wider border border-white/30 inline-block">
                Limited Time Promo
              </span>
              <h3 className="text-2xl sm:text-4xl font-black">{countdownBanner.title}</h3>
              <p className="text-blue-100 text-xs sm:text-sm max-w-md">
                {countdownBanner.subtitle || "Save up to 35% on seasonal items & verified essentials."}
              </p>
            </div>
            <Link
              href={countdownBanner.target_url || "/products"}
              className="px-8 py-3.5 rounded-full bg-white text-blue-700 hover:bg-blue-50 text-xs font-black tracking-wide shadow-xl transition-all inline-flex items-center gap-2"
            >
              <span>{countdownBanner.button_text || "Grab Offer Now"}</span>
              <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </section>
      )}

      {/* 🌟 ৫. Deals of the Day */}
      {deals.length > 0 && (
        <section className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="flex items-center gap-2.5 mb-6">
            <div className="w-9 h-9 rounded-2xl bg-amber-500/10 text-amber-500 flex items-center justify-center">
              <Flame className="w-5 h-5 fill-amber-500" />
            </div>
            <div>
              <h2 className="text-xl sm:text-2xl font-black text-slate-900">Deals of the Day</h2>
              <p className="text-xs text-slate-400">Exclusive flash pricing for today only</p>
            </div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {deals.map((item) => {
              const itemImg = resolveImg(
                item.image_url || item.image,
                "https://images.unsplash.com/photo-1542838132-92c53300491e?w=500"
              );
              return (
                <div
                  key={item.id}
                  className="bg-white rounded-3xl p-5 shadow-[8px_8px_18px_#dce5ef,-8px_-8px_18px_#ffffff] border border-white/80 space-y-3 hover:-translate-y-1 transition-transform"
                >
                  <div className="relative w-full h-44 rounded-2xl overflow-hidden bg-slate-50">
                    <Image
                      src={itemImg}
                      alt={item.name}
                      fill
                      unoptimized
                      className="object-cover"
                    />
                    <span className="absolute top-2.5 left-2.5 px-2.5 py-1 rounded-full bg-amber-500 text-white text-[9px] font-black uppercase">
                      Hot Deal
                    </span>
                  </div>
                  <h3 className="text-xs font-bold text-slate-900 truncate">{item.name}</h3>
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-black text-blue-600 font-mono">
                      ৳{Number(item.price || 0).toLocaleString()}
                    </span>
                    <Link
                      href={`/products/${item.slug || item.id}`}
                      className="text-xs font-bold text-blue-600 hover:underline"
                    >
                      View Details →
                    </Link>
                  </div>
                </div>
              );
            })}
          </div>
        </section>
      )}

      {/* 🌟 ৬. Trust Badges */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {[
            { icon: Truck, title: "Super Fast Delivery", desc: "Doorstep drop in 24 hrs" },
            { icon: ShieldCheck, title: "100% Authentic", desc: "Verified merchant goods" },
            { icon: RotateCcw, title: "Instant Returns", desc: "Hassle-free 7 day replacement" },
            { icon: Headphones, title: "24/7 Support", desc: "Call or chat anytime" },
          ].map((badge, idx) => {
            const Icon = badge.icon;
            return (
              <div
                key={idx}
                className="bg-white rounded-3xl p-5 shadow-[6px_6px_14px_#dce5ef,-6px_-6px_14px_#ffffff] border border-white flex items-center gap-3.5"
              >
                <div className="w-10 h-10 rounded-2xl bg-blue-50 text-blue-600 flex items-center justify-center flex-shrink-0">
                  <Icon className="w-5 h-5" />
                </div>
                <div>
                  <h4 className="text-xs font-black text-slate-900">{badge.title}</h4>
                  <p className="text-[10px] text-slate-400 mt-0.5">{badge.desc}</p>
                </div>
              </div>
            );
          })}
        </div>
      </section>

      {/* 🌟 ৭. Reviews & Testimonials */}
      {testimonials.length > 0 && (
        <section className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="text-center mb-8">
            <h2 className="text-2xl font-black text-slate-900">What Customers Say</h2>
            <p className="text-xs text-slate-400 mt-1">Real reviews from verified shoppers</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {testimonials.slice(0, 3).map((t: any) => (
              <div
                key={t.id}
                className="bg-white rounded-3xl p-6 shadow-[8px_8px_18px_#dce5ef,-8px_-8px_18px_#ffffff] border border-white space-y-3"
              >
                <div className="flex items-center gap-1 text-amber-400">
                  {[...Array(t.rating || 5)].map((_, i) => (
                    <Star key={i} className="w-4 h-4 fill-amber-400" />
                  ))}
                </div>
                <p className="text-xs text-slate-600 leading-relaxed italic">
                  &ldquo;{t.comment}&rdquo;
                </p>
                <div className="pt-2 border-t border-slate-100 flex items-center justify-between">
                  <div>
                    <h4 className="text-xs font-black text-slate-900">{t.customer_name}</h4>
                    <span className="text-[10px] text-slate-400">{t.designation || "Shopper"}</span>
                  </div>
                  <CheckCircle2 className="w-4 h-4 text-emerald-500" />
                </div>
              </div>
            ))}
          </div>
        </section>
      )}

      {/* 🌟 ৮. Stories & Blogs */}
      {blogs.length > 0 && (
        <section className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="text-center mb-8">
            <h2 className="text-2xl font-black text-slate-900">Stories & Updates</h2>
            <p className="text-xs text-slate-400 mt-1">Tips, shopping tricks and lifestyle blogs</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {blogs.slice(0, 3).map((post: any) => {
              const blogImg = resolveImg(
                post.cover_image_url || post.cover_image,
                "https://images.unsplash.com/photo-1542838132-92c53300491e?w=500"
              );
              return (
                <div
                  key={post.id}
                  className="bg-white rounded-3xl p-4 shadow-[8px_8px_18px_#dce5ef,-8px_-8px_18px_#ffffff] border border-white flex flex-col justify-between"
                >
                  <div>
                    <div className="relative w-full h-44 rounded-2xl overflow-hidden mb-3">
                      <Image
                        src={blogImg}
                        alt={post.title}
                        fill
                        unoptimized
                        className="object-cover"
                      />
                    </div>
                    <span className="text-[10px] font-bold text-blue-600 uppercase">
                      {post.category_tag || "General"}
                    </span>
                    <h3 className="text-sm font-black text-slate-900 mt-1 line-clamp-2">
                      {post.title}
                    </h3>
                    <p className="text-xs text-slate-500 mt-1 line-clamp-2">{post.summary}</p>
                  </div>
                  <div className="pt-3 mt-3 border-t border-slate-100 flex items-center justify-between text-[11px] text-slate-400">
                    <span className="flex items-center gap-1">
                      <Calendar className="w-3 h-3" />
                      <span>{post.published_at ? String(post.published_at).slice(0, 10) : "Recent"}</span>
                    </span>
                    <span className="font-bold text-blue-600 hover:underline cursor-pointer">Read →</span>
                  </div>
                </div>
              );
            })}
          </div>
        </section>
      )}

      {/* 🌟 ৯. FAQ Accordion */}
      {faqs.length > 0 && (
        <section className="max-w-4xl mx-auto px-4">
          <div className="text-center mb-8">
            <h2 className="text-2xl font-black text-slate-900">Frequently Asked Questions</h2>
            <p className="text-xs text-slate-400 mt-1">Everything you need to know about deliveries and orders</p>
          </div>
          <div className="space-y-3.5">
            {faqs.map((faq: any) => {
              const isOpen = activeFaq === faq.id;
              return (
                <div
                  key={faq.id}
                  className="bg-white rounded-2xl shadow-[6px_6px_14px_#dce5ef,-6px_-6px_14px_#ffffff] border border-white overflow-hidden transition-all"
                >
                  <button
                    onClick={() => setActiveFaq(isOpen ? null : faq.id)}
                    className="w-full p-4 text-left flex items-center justify-between gap-4 cursor-pointer"
                  >
                    <span className="text-xs sm:text-sm font-bold text-slate-800">
                      {faq.question}
                    </span>
                    <ChevronDown
                      className={`w-4 h-4 text-slate-400 transition-transform ${
                        isOpen ? "rotate-180 text-blue-600" : ""
                      }`}
                    />
                  </button>
                  {isOpen && (
                    <div className="px-4 pb-4 pt-1 text-xs text-slate-500 leading-relaxed border-t border-slate-50">
                      {faq.answer}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </section>
      )}

      {/* 🌟 ১০. Newsletter Card */}
      <section className="max-w-4xl mx-auto px-4 pt-4">
        <div className="bg-gradient-to-r from-blue-600 to-indigo-600 rounded-[36px] p-8 sm:p-12 text-center text-white shadow-2xl shadow-blue-500/25 space-y-4">
          <h3 className="text-2xl sm:text-3xl font-black">
            Stay Updated with {settings?.store_name || subdomain}
          </h3>
          <p className="text-blue-100 text-xs sm:text-sm max-w-md mx-auto">
            Subscribe to our weekly alerts and get special discount coupons on your next order.
          </p>
          <form
            onSubmit={(e) => {
              e.preventDefault();
              alert("Subscribed successfully!");
            }}
            className="max-w-md mx-auto flex items-center bg-white rounded-full p-1.5 shadow-xl"
          >
            <input
              type="email"
              required
              placeholder="Enter your email..."
              className="flex-1 px-4 text-xs text-slate-800 outline-none"
            />
            <button
              type="submit"
              className="px-6 py-2.5 rounded-full bg-slate-900 hover:bg-slate-800 text-white text-xs font-bold transition-all cursor-pointer"
            >
              Subscribe
            </button>
          </form>
        </div>
      </section>
    </div>
  );
}